import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { LayoutDashboard, Users, GraduationCap, UploadCloud, FilePlus2, Bell, FileBadge, FolderSearch, Building2, FileCheck2, Inbox, KeyRound, UserCog, Award } from "lucide-react";

const ORG_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/workspace/employees", label: "Employees", icon: Users },
  { to: "/workspace/employees?tab=claims", label: "Pending Claims", icon: Bell, match: "/workspace/employees" },
  { to: "/workspace/employees/add", label: "Add Employee", icon: FilePlus2 },
  { to: "/workspace/employees/bulk", label: "Bulk Upload", icon: UploadCloud },
  { to: "/workspace/certificates/issue", label: "Issue Certificate", icon: FileCheck2 },
  { to: "/workspace/approvals", label: "Approval Inbox", icon: Inbox },
  { to: "/workspace/staff", label: "HR Staff & Roles", icon: UserCog },
  { to: "/certificates", label: "Certificates", icon: FileBadge },
  { to: "/workspace/settings/approval", label: "Approval Settings", icon: KeyRound },
  { to: "/org-profile", label: "Organization Profile", icon: Building2 },
];

const INST_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/workspace/students", label: "Students", icon: GraduationCap },
  { to: "/workspace/students?tab=claims", label: "Pending Claims", icon: Bell, match: "/workspace/students" },
  { to: "/workspace/students/add", label: "Add Student", icon: FilePlus2 },
  { to: "/workspace/students/bulk", label: "Bulk Upload", icon: UploadCloud },
  { to: "/workspace/certificates/issue", label: "Issue Certificate", icon: FileCheck2 },
  { to: "/workspace/approvals", label: "Approval Inbox", icon: Inbox },
  { to: "/workspace/my-awards", label: "My Awards & Badges", icon: Award },
  { to: "/workspace/staff", label: "HR Staff & Roles", icon: UserCog },
  { to: "/workspace/settings/approval", label: "Approval Settings", icon: KeyRound },
  { to: "/certificates", label: "Certificates", icon: FileBadge },
  { to: "/org-profile", label: "Institution Profile", icon: Building2 },
];

const IND_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/passport", label: "Passport", icon: FileBadge },
  { to: "/my-records", label: "My Records", icon: FolderSearch },
  { to: "/certificates", label: "Certificates", icon: FileBadge },
];

export default function WorkspaceLayout({ children, title, subtitle, actions }) {
  const { user } = useAuth();
  const role = user?.role;
  const nav = role === "organization" || role === "verifier" || role === "super_admin"
    ? ORG_NAV
    : role === "institution"
    ? INST_NAV
    : IND_NAV;
  const loc = useLocation();
  const here = loc.pathname + loc.search;

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="workspace-layout">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8 grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block" data-testid="workspace-sidebar">
          <div className="eyebrow mb-4">Workspace</div>
          <nav className="space-y-0.5">
            {nav.map((item) => {
              const active = item.match ? loc.pathname.startsWith(item.match) && here === item.to : here === item.to || loc.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm border-l-2 transition-colors ${active ? "border-ec-emerald bg-ec-emerald/5 font-semibold text-ec-ink" : "border-transparent text-ec-ink-soft hover:text-ec-ink hover:bg-ec-mute"}`}
                  data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0">
          {(title || actions) && (
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                {subtitle && <div className="eyebrow">{subtitle}</div>}
                {title && <h1 className="font-display text-4xl font-black tracking-tight mt-2">{title}</h1>}
              </div>
              {actions}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
